import { useState, useRef, useEffect } from 'react';
import { Camera, Loader2, AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import api from '../../api/axios';

interface EndDayModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export function EndDayModal({ isOpen, onClose, onSuccess }: EndDayModalProps) {
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [geoError, setGeoError] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const videoRef = useRef<HTMLVideoElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);

    async function startCamera() {
        setCameraError(null);
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({ 
                video: { facingMode: 'user' } 
            });
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                videoRef.current.play().catch(e => console.warn('Play error:', e));
            }
        } catch (_err) {
            setCameraError('Не удалось получить доступ к камере. Разрешите доступ в браузере.');
        }
    }

    function stopCamera() {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    }

    useEffect(() => {
        if (isOpen) {
            startCamera();
        } else {
            stopCamera();
        }
        return () => stopCamera();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const takePhoto = (): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            if (!videoRef.current) return reject('No video');
            
            const canvas = document.createElement('canvas');
            const video = videoRef.current;
            
            // max size 800px
            const maxDim = 800;
            let width = video.videoWidth;
            let height = video.videoHeight;
            
            if (width > height) {
                if (width > maxDim) {
                    height *= maxDim / width;
                    width = maxDim;
                }
            } else {
                if (height > maxDim) {
                    width *= maxDim / height;
                    height = maxDim;
                }
            }
            
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(video, 0, 0, width, height);
            
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject('Failed to create blob');
            }, 'image/webp', 0.8);
        });
    };

    async function handleEndDay() {
        setIsProcessing(true);
        setGeoError(null);
        
        try {
            const photoBlob = await takePhoto();
            
            const formData = new FormData();
            formData.append('file', photoBlob, 'selfie.webp');
            
            const uploadRes = await api.post('/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadData = uploadRes.data;
            
            if (!uploadData.url) throw new Error('Ошибка загрузки фото');
            
            await api.post('/attendance/check-out', {
                photo_url: uploadData.url
            });
            
            stopCamera();
            onSuccess();
            onClose();
        } catch (e: unknown) {
            setGeoError(e instanceof Error ? e.message : 'Произошла ошибка (Убедитесь, что день был начат)');
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-110 bg-black/60 flex items-center justify-center p-4 overflow-y-auto w-full h-full">
            <Card className="w-full max-w-md p-6 flex flex-col items-center gap-6 shadow-xl relative mt-10">
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 bg-slate-100 rounded-full text-slate-500 hover:bg-slate-200 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
                <div className="text-center space-y-2 mt-4">
                    <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Завершить смену</h1>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        Сделайте фото для подтверждения завершения работы.
                    </p>
                </div>

                {/* Camera View */}
                <div className="relative w-40 h-40 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-800 border-4 border-slate-300 dark:border-slate-700 flex items-center justify-center">
                    <video 
                        ref={videoRef} 
                        autoPlay
                        playsInline 
                        muted 
                        className={`w-full h-full object-cover scale-x-[-1] absolute inset-0 ${cameraError ? 'opacity-0 z-[-1]' : 'opacity-100 z-10'}`} 
                    />

                    {cameraError && (
                        <div className="text-center p-4 absolute inset-0 flex flex-col items-center justify-center bg-slate-200 dark:bg-slate-800 z-20">
                            <Camera className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                            <span className="text-[10px] text-rose-500 font-medium">Камера недоступна</span>
                        </div>
                    )}
                </div>

                {geoError && (
                    <Alert variant="destructive" className="bg-rose-50 dark:bg-rose-950/20 text-rose-600 dark:text-rose-400 text-xs">
                        <AlertTriangle className="w-4 h-4" />
                        <AlertDescription>{geoError}</AlertDescription>
                    </Alert>
                )}

                <Button 
                    variant="destructive"
                    className="w-full h-12 text-base font-semibold" 
                    onClick={handleEndDay}
                    disabled={isProcessing || !!cameraError}
                >
                    {isProcessing ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                    {isProcessing ? 'Завершение...' : 'Закончить день'}
                </Button>
            </Card>
        </div>
    );
}
